import { assign, createMachine } from "xstate";
import {
  IChangeSimEsimOrchestrator,
  IMobileOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { setSimCard } from "@nju/data-access-portal-sim-card";
import { ISimCardFlow } from "../types";

export interface IContext {
  sim: string;
  flow: ISimCardFlow;
  simError?: string;
  orchestratorId?: string;
}

type IEvent =
  | {
      type: "SET_SIM";
      value: string;
    }
  | {
      type: "SIM_SET_SUCCESS";
      orchestrator: IMobileOrchestrator | IChangeSimEsimOrchestrator;
    }
  | { type: "WRONG_SIM_CODE" }
  | { type: "GENERAL_ERROR" }
  | { type: "SUBMIT" };

export const mobileSimCardMachine = createMachine(
  {
    context: {
      sim: "",
      flow: "ONBOARDING",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_SIM: {
            actions: "setSim",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoSimCodeErrors",
            target: "setSimCard",
          },
          {
            target: "ready",
          },
        ],
      },
      setSimCard: {
        invoke: {
          src: "setSimCard",
        },
        on: {
          SIM_SET_SUCCESS: {
            actions: "setSimSetSuccess",
            target: "final",
          },
          WRONG_SIM_CODE: {
            target: "wrongSimCode",
          },
          GENERAL_ERROR: {
            target: "generalError",
          },
        },
      },
      wrongSimCode: {
        type: "final",
      },
      generalError: {
        type: "final",
      },
      final: {
        type: "final",
      },
    },
  },
  {
    services: {
      setSimCard: ({ orchestratorId, flow, sim }) => {
        return async (callback) => {
          if (!orchestratorId) {
            throw new Error("Missing orchestratorId");
          }

          const result = await setSimCard({
            mode: flow === "ONBOARDING" ? "mobile" : "changeSimEsim",
            orchestratorId,
            simOrIccId: sim,
          });

          if (result.isError) {
            if (result.err.code === "WRONG_SIM_CODE") {
              return callback({ type: "WRONG_SIM_CODE" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          callback({
            type: "SIM_SET_SUCCESS",
            orchestrator: result.value,
          });
        };
      },
    },
    guards: {
      hasNoSimCodeErrors: ({ simError }) => {
        return simError === undefined;
      },
    },
    actions: {
      validateForm: assign<IContext, IEvent>({
        simError: ({ sim }) => {
          if (!/^\d{4}$/.test(sim)) {
            return "To nie są 4 cyfry! Wpisz tylko 4 ostatnie cyfry numeru.";
          }

          return;
        },
      }),
      setSim: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_SIM") {
          return {
            sim: event.value,
          };
        }

        return context;
      }),
    },
  },
);
